import React, { createContext, PropsWithChildren, useContext } from 'react';
import { AxiosError } from 'axios';
import { useSortableList } from '../components/SortableContentList/useSortableList';
import { DragAndDropProvider } from './DragAndDropProvider';

type SortableListContext = { hasDarkDragLayerBackground?: boolean } & Omit<
  ReturnType<typeof useSortableList>,
  'sortableItems'
>;

export const SortableListContext = createContext<SortableListContext | undefined>(undefined);

export function SortableListContextProvider({
  children,
  ...context
}: PropsWithChildren<SortableListContext>) {
  return (
    <SortableListContext.Provider value={context}>
      <DragAndDropProvider>{children}</DragAndDropProvider>
    </SortableListContext.Provider>
  );
}

export function useSortableListContext() {
  const value = useContext(SortableListContext);
  if (value === undefined) {
    throw Error('SortableListContext is used without Provider');
  }
  return value as SortableListContext;
}

export function useSortableListItem<TError = unknown>(id: string) {
  const { move, getPrevItem, getNextItem, error, findCurrentItemIndex } = useSortableListContext();
  return {
    move: (targetId: string) => move(id, targetId),
    prevItemId: getPrevItem(id),
    nextItemId: getNextItem(id),
    error: id === error?.id ? (error.error as AxiosError<TError>) : undefined,
    currentItemIndex: findCurrentItemIndex(id),
  };
}
